// paymentClock.ts — the payment clock's vocabulary and wording, shared by the
// vendor form, the PO create form, the PO page and the billing rail.
//
// The ARITHMETIC lives server-side (api/src/procurement/payment-clock.js) and
// comes back computed as `payment_clock` on the PO detail and `terms_due_date`
// on each billing-rail invoice. Nothing here adds days to a date. Behavior:
// web/docs/features/purchase-orders.md, "Payment clock".

export type PaymentTermsBasis = 'order' | 'ship' | 'receipt' | 'invoice' | 'invoice_eom';

/** Mirrors the DB CHECK (migration 078) and the server's PAYMENT_TERMS_BASES. */
export const PAYMENT_TERMS_BASES: PaymentTermsBasis[] = ['invoice', 'invoice_eom', 'ship', 'receipt', 'order'];

/** Dropdown labels: what the clock starts from, in the buyer's words. */
export const PAYMENT_BASIS_LABELS: Record<PaymentTermsBasis, string> = {
  invoice: 'Invoice date',
  invoice_eom: 'End of invoice month',
  ship: 'Vendor ship date',
  receipt: 'Goods arriving',
  order: 'Placing the order',
};

/** The same anchors mid-sentence: "Net 30 from the vendor ship date". */
const BASIS_IN_PROSE: Record<PaymentTermsBasis, string> = {
  invoice: 'the invoice date',
  invoice_eom: 'the end of the invoice month',
  ship: 'the vendor ship date',
  receipt: 'the goods arriving',
  order: 'placing the order',
};

/** GET /procurement/purchase-orders/:id → payment_clock. Computed server-side
 *  from the order's own snapshot (terms_days / terms_basis) and whichever date
 *  that basis reads. */
export interface PaymentClock {
  days: number | null;
  basis: PaymentTermsBasis | null;
  /** The date the clock started from, once known. */
  anchor_date: string | null; // YYYY-MM-DD
  due_date: string | null; // YYYY-MM-DD
  /** An ESTIMATE: the same days counted from the order date, offered only
   *  while a ship or receipt clock has no anchor yet. Usually the earliest the
   *  bill could fall due, but not guaranteed (backfilled paper can carry an
   *  order date after the real ship date), so the page says "Estimated" and
   *  never "no earlier than". Kept apart from due_date so an estimate is never
   *  read as the date. */
  estimated_due_date: string | null; // YYYY-MM-DD
  /** What is still missing when due_date is null on a SET clock. 'invoice'
   *  means the clock resolves per bill on the billing rail, not here. */
  waiting_on: 'order' | 'ship' | 'receipt' | 'invoice' | null;
}

export function isPaymentTermsBasis(value: unknown): value is PaymentTermsBasis {
  return typeof value === 'string' && (PAYMENT_TERMS_BASES as string[]).includes(value);
}

/** "Net 30 from the vendor ship date", or null when either half is missing. */
export function describeClock(days: number | null, basis: PaymentTermsBasis | null): string | null {
  if (days === null || basis === null) return null;
  const net = days === 0 ? 'Due immediately' : `Net ${days}`;
  return `${net} from ${BASIS_IN_PROSE[basis]}`;
}

/** Why the date shown is only an estimate, naming the field that would fix it.
 *  The missing field is the point: this line is what asks for a ship date. */
export function estimateReason(waitingOn: PaymentClock['waiting_on']): string | null {
  switch (waitingOn) {
    case 'ship': return 'No ship date entered, so this counts from the order date.';
    case 'receipt': return 'Nothing received yet, so this counts from the order date.';
    default: return null;
  }
}

/** Why there is no due date yet, in a phrase that fits after the clock. */
export function waitingLabel(waitingOn: PaymentClock['waiting_on']): string | null {
  switch (waitingOn) {
    case 'ship': return 'waits for the ship date';
    case 'receipt': return 'waits for the first delivery';
    case 'order': return 'waits for the order date';
    case 'invoice': return 'counts from each invoice, see Billing';
    default: return null;
  }
}
